import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import { PRODUCT_ICON_MAP } from '../product-subpage/productIcons';
import type { SolutionOffering } from './types';

const META_ICONS = {
  whyItMatters: '/images/solutions/smart-house/shield-star.svg',
  targetMarket: '/images/solutions/smart-house/target-market.svg',
} as const;

const FALLBACK_VISUALS = [
  { src: '/images/home/platform-dashboard-ui.png', alt: 'Platform dashboard preview', width: 1200, height: 800 },
  { src: '/images/home/platform-phone.png', alt: 'Mobile app preview', width: 900, height: 1200 },
] as const;

function toBullets(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];

  const fromNewlines = trimmed
    .split(/\r?\n+/)
    .map((s) => s.trim())
    .filter(Boolean);
  if (fromNewlines.length > 1) return fromNewlines;

  const fromCommonSeparators = trimmed
    .split(/\s*(?:•|\u2022|\u25CF|-|\u2013|\u2014|;)\s*/g)
    .map((s) => s.trim())
    .filter(Boolean);
  if (fromCommonSeparators.length > 1) return fromCommonSeparators.slice(0, 4);

  // Gentle sentence split; keep it short for scannability.
  const sentences = trimmed
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return (sentences.length > 1 ? sentences : [trimmed]).slice(0, 3);
}

export const SolutionOfferingsShowcase: React.FC<{
  offerings: readonly SolutionOffering[];
}> = ({ offerings }) => (
  <SiteSection className="solution-offerings solution-offerings--showcase solution-core-modules">
    <SiteSectionHeader
      eyebrow="CORE MODULES"
      title="Purpose-built offerings for property partners"
      subtitle="Deploy access, protection, and in-unit automation as branded modules your residents love."
      align="center"
    />
    <div className="solution-core-modules__list">
      {offerings.map((item, index) => {
        const Icon = PRODUCT_ICON_MAP[item.icon];
        const visual =
          item.image ?? FALLBACK_VISUALS[index % FALLBACK_VISUALS.length];
        const isReversed = index % 2 === 0; // Access Control right, image left (match request)
        const whyBullets = toBullets(item.whyItMatters);
        const marketBullets = toBullets(item.targetMarket);
        return (
          <section
            key={item.title}
            className={`solution-core-module${isReversed ? ' solution-core-module--reverse' : ''}`}
          >
            <div className="solution-core-module__visual" aria-hidden>
              <img
                src={visual.src}
                alt={visual.alt}
                width={visual.width}
                height={visual.height}
                loading="lazy"
              />
            </div>

            <article className="solution-core-module__content">
              <div className="solution-core-module__kicker">
                <span className="solution-core-module__icon" aria-hidden>
                  <Icon size={22} strokeWidth={1.75} />
                </span>
                <h3 className="solution-core-module__title">{item.title}</h3>
              </div>
              <p className="solution-core-module__desc">{item.description}</p>

              <div className="solution-core-module__meta">
                <div className="solution-core-module__meta-block">
                  <div className="solution-core-module__meta-head">
                    <div className="solution-core-module__meta-icon" aria-hidden>
                      <img src={META_ICONS.whyItMatters} alt="" width={24} height={24} loading="lazy" />
                    </div>
                    <h4 className="solution-core-module__meta-title">Why it matters</h4>
                  </div>
                  <ul className="solution-core-module__bullets">
                    {whyBullets.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
                <div className="solution-core-module__meta-block">
                  <div className="solution-core-module__meta-head">
                    <div className="solution-core-module__meta-icon" aria-hidden>
                      <img src={META_ICONS.targetMarket} alt="" width={24} height={24} loading="lazy" />
                    </div>
                    <h4 className="solution-core-module__meta-title">Target market</h4>
                  </div>
                  <ul className="solution-core-module__bullets">
                    {marketBullets.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </article>
          </section>
        );
      })}
    </div>
  </SiteSection>
);
